import React, { useEffect, useId, useState } from "react"
import { Collapse, Divider, List, ListItemButton, ListItemText, ListItemIcon } from "@mui/material"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Link } from "react-router-dom"
import { routes } from "@/shared/constants"

interface Props {
  currentPath: string
  onNavigate: () => void
}

type NavigationItem = (typeof routes)[number]

const CalculatorLinks: React.FC<Props & { link: NavigationItem }> = ({ link, currentPath, onNavigate }) => {
  const active = currentPath === link.to || currentPath.startsWith(`${link.to}/`)
  const [expanded, setExpanded] = useState(active)
  const submenuId = useId()
  const Icon = link.icon

  useEffect(() => {
    if (active) setExpanded(true)
  }, [active, currentPath])

  return (
    <>
      <ListItemButton onClick={() => setExpanded(value => !value)} aria-expanded={expanded} aria-controls={submenuId}>
        <ListItemIcon>
          <Icon />
        </ListItemIcon>
        <ListItemText primary={link.text} />
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit id={submenuId}>
        <List disablePadding aria-label={link.text}>
          {link.children?.map(child => {
            const ChildIcon = child.icon
            const selected =
              child.to === link.to ? active && !currentPath.startsWith(`${link.to}/chart`) : currentPath === child.to
            return (
              <ListItemButton
                key={child.to}
                component={Link}
                to={child.to}
                selected={selected}
                aria-current={selected ? "page" : undefined}
                onClick={onNavigate}
                sx={{ pl: 4 }}>
                <ListItemIcon>
                  <ChildIcon />
                </ListItemIcon>
                <ListItemText primary={child.text} />
              </ListItemButton>
            )
          })}
        </List>
      </Collapse>
    </>
  )
}

const NavLinks: React.FC<Props> = ({ currentPath, onNavigate }) => (
  <List sx={{ width: "100%", padding: 0, margin: 0, maxWidth: 600, bgcolor: "background.paper" }}>
    {routes.map((link, index) => {
      const Icon = link.icon
      return (
        <React.Fragment key={link.to}>
          {link.children ? (
            <CalculatorLinks link={link} currentPath={currentPath} onNavigate={onNavigate} />
          ) : (
            <ListItemButton selected={currentPath === link.to} component={Link} to={link.to} onClick={onNavigate}>
              <ListItemIcon>
                <Icon />
              </ListItemIcon>
              <ListItemText primary={link.text} />
            </ListItemButton>
          )}
          {index < routes.length - 1 && <Divider component="li" />}
        </React.Fragment>
      )
    })}
  </List>
)

export default NavLinks
